import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Text from "components/atoms/text/Text";

const Container = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: ${(props) => props.margin};
    width: ${(props) => props.width};
    max-width: ${(props) => props.maxWidth};
    height: ${(props) => (props.height ? props.height : "32px")};
    background-color: ${(props) => (props.disabled ? "#F2F6F8" : "var(--color-White)")};
    border: ${(props) => (props.isOpen ? "solid 1px var(--color-Key)" : "solid 1px var(--color-Outline)")};
    border-radius: 4px;
`;

const SelectedItem = styled.div`
    display: flex;
    align-items: center;
    margin: 0 4px 0 0;
    padding: 0 0 0 12px;
    width: calc(100% - 24px);
    max-width: calc(100% - 24px);
    height: 100%;
    word-break: break-all;
`;

const IconBox = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 10px 0 10px;
    width: 10px;
    height: 10px;
    transform: rotate(${(props) => props.reverse || "0deg"});
`;

const OptionContainer = styled.div`
    position: absolute;
    ${(props) => props.topBottom || "top"}: 36px;
    ${(props) => (props.optionAlign === "left" ? "left: -2px;" : "right: 0;")}
    right: 0;
    width: ${(props) => props.optionWidth || "100%"};
    min-width: ${(props) => props.optionMinWidth};
    height: ${(props) => (props.isOpen ? "auto" : "0")};
    opacity: ${(props) => (props.isOpen ? "1" : "0")};
    visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
    overflow: hidden;
    transition: all 100ms ease-in;
    z-index: 100;
    ${(props) => props.top && `top: ${props.top};`}
`;

const OptionWrapper = styled.div`
    width: 100%;
    padding: 4px 2px 3px 6px;
    background-color: var(--color-White);
    border-radius: 4px;
    border: solid 1px var(--color-Key);
`;

const OptionList = styled.ul`
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    max-height: ${(props) => props.optionMaxHeight || "120px"};
    border-radius: 2px;
    overflow: auto;

    &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
        display: initial;
    }

    &::-webkit-scrollbar-thumb {
        min-width: 40px;
        min-height: 20px;
        background-color: var(--color-DarkKey);
    }

    &::-webkit-scrollbar-track {
        background-color: var(--color-ButtonHover4);
    }
`;

const OptionItem = styled.li`
    width: 100%;
    height: 32px;
    list-style: none;
`;

const OptionItemInner = styled.div`
    display: flex;
    align-items: center;
    margin: 1px 4px 1px 0;
    padding: 0 8px;
    width: calc(100% - 4px);
    height: 30px;
    border-radius: 2px;
    border: transparent;
    background-color: var(--color-White);
    transition: all 50ms ease-in;

    &:hover {
        background-color: var(--color-ButtonHover4);
        cursor: pointer;
    }
`;

const OptionItemText = styled(Text)`
    width: 100%;
    font-size: 1.4rem;
    text-align: ${(props) => props.textAlign};

    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const SelectedOptionItemInner = styled(OptionItemInner)`
    background-color: var(--color-Key) !important;

    p {
        color: var(--color-White);
    }
`;

function SelectBox(props) {
    const selectedOptionName = props.items?.find((item) => item.id === props.value)?.name;

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // 영역 외 영역을 클릭하면 드롭다운 닫힘
    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        window.addEventListener("mousedown", handleOutsideClick);

        return () => {
            window.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    return (
        <Container
            width={props.width}
            height={props.height}
            maxWidth={props.maxWidth}
            margin={props.margin}
            disabled={props.disabled}
            onClick={(e) => {
                e.stopPropagation();
                setIsOpen(props.disabled ? false : !isOpen);
            }}
            onChange={props.onChange}
            isOpen={isOpen}
            ref={dropdownRef}
        >
            <SelectedItem>
                <OptionItemText textAlign={props.textAlign || "center"}>{selectedOptionName || ""}</OptionItemText>
            </SelectedItem>
            <IconBox reverse={props.reverse}>
                <Icon name={isOpen ? "arrowUp" : "arrowDown"} color={"var(--color-SubBlack)"} />
            </IconBox>
            <OptionContainer
                optionWidth={props.optionWidth}
                optionMinWidth={props.optionMinWidth}
                optionAlign={props.optionAlign}
                isOpen={isOpen}
                topBottom={props.topBottom}
                top={props.top}
            >
                <OptionWrapper>
                    <OptionList optionMaxHeight={props.optionMaxHeight}>
                        {props.items?.map((item, index) => (
                            <OptionItem
                                key={index}
                                value={item.id}
                                onClick={(e) => {
                                    props.onChange(item.id);
                                    setIsOpen(false);
                                }}
                            >
                                {props.value === item.id ? (
                                    <SelectedOptionItemInner>
                                        <OptionItemText textAlign={props.textAlign || "center"}>
                                            {item.name || item}
                                        </OptionItemText>
                                    </SelectedOptionItemInner>
                                ) : (
                                    <OptionItemInner>
                                        <OptionItemText textAlign={props.textAlign || "center"}>
                                            {item.name || item}
                                        </OptionItemText>
                                    </OptionItemInner>
                                )}
                            </OptionItem>
                        ))}
                    </OptionList>
                </OptionWrapper>
            </OptionContainer>
        </Container>
    );
}

export default SelectBox;
